import { useContext } from "react";
import { Box, Card, Chip, Divider, Stack, Typography } from "@mui/material";
import { Airplane, Calendar, House, Location, Moon } from "iconsax-react";
import { BaseButton } from "../../common/base-button/base-button";
import { useTranslation } from "react-i18next";
import { numberWithCommas } from "../../../utils/utility";
import { useNavigate } from "react-router-dom";
import { LocaleContext } from "../../../routing/LangRouter";

export default function CountryTourItem({ tour }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);

    const goToTourDetail = tourId => {
        const tourName = {
            1: "winter",
            2: "spring",
        };
        navigate(`/${locale}/country-tour-detail?tour=${tourName[tourId]}`);
    };

    const details = [
        {
            icon: Calendar,
            label: "date",
            value: tour.departureDate,
        },
        {
            icon: Moon,
            label: "tripDuration",
            value: tour.duration,
        },
        {
            icon: Airplane,
            label: "transfer",
            value: tour.transport,
        },
        {
            icon: House,
            label: "accommodation",
            value: tour.hotel,
        },
    ];

    return (
        <Card
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "background.paper",
                transition: "all .25s ease",
                display: "flex",
                flexDirection: "column",

                "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: "0 10px 35px rgba(0, 60, 60, 0.08)",
                    transform: "translateY(-3px)",
                },
            }}
        >
            {/* Image */}
            <Box
                sx={{
                    position: "relative",
                    // minHeight: {
                    //     xs: 210,
                    //     md: 240,
                    // },
                    overflow: "hidden",
                }}
            >
                <Box
                    component="img"
                    src={tour.image}
                    alt={tour.title}
                    sx={{
                        width: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform .4s ease",
                        ".MuiCard-root:hover &": {
                            transform: "scale(1.05)",
                        },
                    }}
                />

                <Chip
                    label={t(tour.tourType)}
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        bgcolor: "rgba(255,255,255,.95)",
                        color: "primary.main",
                        fontWeight: 700,
                        backdropFilter: "blur(8px)",
                    }}
                />
            </Box>

            {/* Main information */}
            <Box
                sx={{
                    p: {
                        xs: 2,
                        md: 2.5,
                    },
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{
                        fontSize: {
                            xs: "1.05rem",
                            md: "1.2rem",
                        },
                    }}
                >
                    {tour.title}
                </Typography>

                <Stack direction="row" alignItems="center" gap={2}>
                    <Location size={20} color="var(--primary-color)" />

                    <Typography variant="caption" color="text.secondary">
                        {tour.country} / {tour.cities.join("، ")}
                    </Typography>
                </Stack>

                <Divider sx={{ mb: 0, borderStyle: "dashed" }} />

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: 2,
                    }}
                >
                    {details.map(item => {
                        const Icon = item.icon;

                        return (
                            <Stack key={item.label} direction="row" alignItems="center" gap={2} minWidth={0}>
                                <Box
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        flexShrink: 0,
                                        borderRadius: 2,
                                        display: "grid",
                                        placeItems: "center",
                                        bgcolor: "action.hover",
                                        color: "primary.main",
                                    }}
                                >
                                    <Icon size={20} color="var(--primary-color)" />
                                </Box>

                                <Box minWidth={0}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {t(item.label)}
                                    </Typography>

                                    <Typography variant="body2" fontWeight={600} noWrap>
                                        {t(item.value)}
                                    </Typography>
                                </Box>
                            </Stack>
                        );
                    })}
                </Box>
            </Box>

            {/* Price section */}
            <Box
                sx={{
                    p: 2.5,
                    bgcolor: "action.hover",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 1.5,
                    borderColor: "divider",
                }}
            >
                {/* <Stack d/ */}

                {/* <Divider /> */}

                <BaseButton
                    text={t("General.Details")}
                    size="medium"
                    radius="radius-10"
                    color="primary"
                    handleClick={() => goToTourDetail(tour.id)}
                />
            </Box>
        </Card>
    );
}
